import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { LessonEditCard } from "@shared/models/analytics/lesson"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type { Card } from "@shared/models/card-stack"
import Patch from "@shared/models/patch-updater"
import { get, isNil } from "lodash-es"
import type { Narrative } from "@shared/actions/create-narrative"
import defaultLessonActivity from "./default-lesson-activity"
import defaultLessonContext from "./default-lesson-context"
import defaultLessonModels from "./default-lesson-models"

interface Payload {
  cardStackId: string
  card: Card
}

export const LESSON_CARD_DELETE = {
  name: "LESSON_CARD_DELETE",

  patches(payload: Payload) {
    if (isNil(payload.card)) throw Error(`Nil Card trying to be deleted on ${payload.cardStackId}`)
    let patch = Patch.create("card-stack", payload.cardStackId).pull("attributes.cards", {
      id: payload.card.id,
    }).patch

    return [patch]
  },

  undoPatches(payload: Payload) {
    let patch = Patch.create("card-stack", payload.cardStackId).addToSet(
      "attributes.cards",
      JSON.parse(JSON.stringify(payload.card))
    ).patch

    return [patch]
  },

  narrative(payload: Payload, findFn: FindFn, userId): Narrative {
    let models = defaultLessonModels(payload, findFn, userId)
    let card = payload.card
    let cardTitle = get(card, "attributes.title")
    let context = defaultLessonContext(payload, models)
    let newContext = Object.assign({}, context, { cardTitle: cardTitle })
    let lessonTitle = models.cardStack!.attributes.title
    let newEvent: LessonEditCard = {
      ...createBlankEvent(),
      root_id: models.planbook?.id ?? null,
      root_type: "planbook",
      date_plan_scheduled_for: models.dateStringForCardStack,
      primary_object_id: payload.cardStackId,
      primary_object_id_and_ancestor_ids: models.cardStackIdAndAncestorIds,
      primary_verb: "edit",
      primary_possessive: getPossessiveForPlanbook(findFn, userId, models.planbook?.id),
      primary_object_type: "lesson",
      primary_object_specifier: "cards",

      adverbial_verb: "removing",
      adverbial_object_type: "card",
      adverbial_object_specifier: null,
      adverbial_object_id: payload.card.id,
      adverbial_possessive: null,
      adverbial_object_id_and_ancestor_ids: models.cardStackIdAndAncestorIds,
    }

    return {
      title: "Deleted a Lesson Card",
      titleForAnalytics: "Lesson Card - Remove",
      description: `Removed "${cardTitle}" card from ${lessonTitle}`,
      context: newContext,
      activity: defaultLessonActivity(payload, models, "removed"),
      newAnalyticsEvents: [newEvent],
    }
  },
}

export default LESSON_CARD_DELETE
